/**
 * Which house documents the signed-in reader may see.
 *
 * `GET /houses/{id}/documents` returns every document in the house with no privacy predicate, and
 * the response carries no `private` field at all — `DocumentBase` does not declare it and the route
 * builds each item field by field without it. So the Records response is not an authorization
 * boundary, and it cannot even be asked whether a document is private.
 *
 * The rule is therefore explicit about what it will trust: only a document that positively states
 * `private: false` is treated as shared. Absent, null and true all fail closed, because the field
 * being missing is exactly the state the live API is in — reading "not private" out of a field that
 * was never sent would make this whole check pass everything through.
 *
 * TODO(schedule-source-visibility): delete this once the list endpoint sends `private` and applies
 * the predicate itself. See docs/schedule/source-visibility.md.
 */

/** The fields the rule reads, so any document-shaped response can be passed. */
export interface VisibilityCandidate {
	id: number;
	private?: boolean | null;
	uploaded_by_id?: number | null;
}

export interface PrivateRecordAccess {
	/**
	 * Holds `HOUSE_DOCUMENT_PRIVATE_ALL`, house admin, or super admin.
	 *
	 * Three rather than one because the permission is not granted anywhere in the backend source; a
	 * reader who is plainly an admin should not lose every source to a permission string that may
	 * never arrive.
	 */
	canSeeEveryPrivateRecord: boolean;
	/** The signed-in user, who may see what they uploaded. Absent while auth resolves. */
	userId: number | undefined;
}

/**
 * The uploader may see their own upload, which is the intended policy.
 *
 * `FileDropOverlay` currently tells a contributor that filing privately hides the record from them.
 * That describes what is built rather than what is meant — the uploader keeping sight of their own
 * file is the intent, and this rule is written to it. The overlay's copy follows when the backend
 * supports it.
 *
 * The identity check is explicit about `undefined`: two absent ids must not match into "mine" while
 * auth is still resolving.
 */
export const canSeeDocument = (document: VisibilityCandidate, access: PrivateRecordAccess): boolean =>
	document.private === false ||
	access.canSeeEveryPrivateRecord ||
	(access.userId !== undefined && document.uploaded_by_id === access.userId);

/** The same rule over a list, as the id set the plan's provenance is filtered against. */
export const visibleDocumentIdsFrom = (
	documents: readonly VisibilityCandidate[],
	access: PrivateRecordAccess,
): ReadonlySet<number> => {
	const ids = new Set<number>();
	for (const document of documents) if (canSeeDocument(document, access)) ids.add(document.id);
	return ids;
};
